/**
 * sceneGallery — the ONE component NSPP ships that no other renderer can match.
 *
 * A product image gallery: 1 large hero + 6 thumbnails. Clicking a thumbnail
 * swaps the hero image. The swap is rendered through the engine's velocity
 * motion blur — the hero image briefly translates ~12px on swap, then snaps
 * back, and the postprocess smears that micro-motion into a visible blur
 * trail. Every luxury brand's gallery uses a CSS opacity crossfade; we
 * give them motion blur. That's the demo.
 *
 * Why this matters as a product wedge: it's ONE component. It doesn't
 * require designing a whole PDP. It drops into any Shopify, WooCommerce,
 * or custom storefront as an `<nspp-gallery>` web component. Designers
 * swap in any 6 product photos and ship.
 *
 * The thumbnails themselves use the same image-as-fill texture path so
 * they get per-instance filter chains (each thumb has its own subtle hue
 * shift). Hovering a thumb brightens it; the selected thumb gets a gold
 * border.
 *
 * Asset note: this scene intentionally ships with ZERO raster assets. The
 * product is a fictional Maison Verre bottle rendered as inline SVG (a
 * SDF bottle silhouette), 6 angle variations written into
 * /static/products/maison-verre/no2/view-*.svg. In production the 6
 * images would be a brand's actual product photography.
 */

import {
  type Scene,
  type SceneNode,
  createScene,
  makeRect,
  makeText,
  addNode,
} from "../sceneGraph";

const VW = 1440;
const VH = 900;

/** The 6 angle variants — same bottle, different palettes per view. */
const VIEWS = [
  { key: "front", label: "Face" },
  { key: "3qtr",  label: "Trois-quarts" },
  { key: "side",  label: "Profil" },
  { key: "back",  label: "Dos" },
  { key: "hero",  label: "Hero" },
  { key: "dark",  label: "Studio" },
];

export function sceneGallery(_w: number, _h: number): Scene {
  const scene = createScene();
  let z = 0;
  const T = () => performance.now();

  // Active thumbnail — drives which image the hero shows.
  let activeIdx = 0;
  // Per-instance shake state: when a thumb is tapped, the hero nudges
  // 12px in a random direction for one frame so the velocity buffer picks
  // it up and the postprocess motion-blur kicks in. This is the trick
  // that turns "image swap" into "motion-blurred image swap."
  let heroShakeUntil = 0;
  let heroShakeX = 0;
  let heroShakeY = 0;

  // ============================================================
  // LAYOUT
  // Hero takes the left ~75% of the canvas. Thumbnails stack on the
  // right with a thin gap between them. Comfortable luxury spacing.
  // ============================================================
  const HERO_PAD = 80;
  const HERO_W = Math.floor((VW - HERO_PAD * 2) * 0.72);
  const HERO_H = VH - HERO_PAD * 2 - 80; // leave room for header at top
  const HERO_X = HERO_PAD;
  const HERO_Y = HERO_PAD + 40;

  const THUMB_W = 88;
  const THUMB_H = 88;
  const THUMB_GAP = 14;
  const THUMBS_X = HERO_X + HERO_W + 48;
  const THUMBS_Y0 = HERO_Y + 20;
  const THUMBS_TOTAL = THUMB_H * VIEWS.length + THUMB_GAP * (VIEWS.length - 1);
  const THUMBS_BLOCK_H = Math.min(THUMBS_TOTAL, HERO_H);

  // ============================================================
  // BACKGROUND
  // ============================================================
  addNode(scene, makeRect({
    x: 0, y: 0, w: VW, h: VH,
    fill: { r: 0.04, g: 0.04, b: 0.05, a: 1 },
    gradientEnd: { r: 0.07, g: 0.05, b: 0.10, a: 1 },
    gradientAngle: Math.PI / 2,
  })); z++;

  // ============================================================
  // HEADER (thin top bar — brand wordmark + product name)
  // ============================================================
  addNode(scene, makeText({
    x: HERO_PAD, y: 32, w: 320, h: 18,
    text: "MAISON VERRE",
    fontSize: 13, fontWeight: 700, letterSpacing: 6,
    textColor: { r: 0.92, g: 0.88, b: 0.78, a: 1 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;
  addNode(scene, makeText({
    x: HERO_PAD, y: 54, w: 600, h: 22,
    text: "Nº 02 — Rose de Soir  ·  eau de parfum",
    fontSize: 15, fontWeight: 500, letterSpacing: 1,
    textColor: { r: 0.62, g: 0.58, b: 0.5, a: 1 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // ============================================================
  // HERO IMAGE WRAPPER (the rect that hosts the image fill texture).
  // We use the engine's imageUrl path so the SVG file at
  // /static/products/maison-verre/no2/view-${VIEWS[activeIdx]}.svg
  // gets uploaded as a texture and sampled as the rect's fill.
  //
  // The shake is applied via update() — when the user taps a thumb we
  // set heroShakeUntil = now + 80ms, and during that window the hero
  // jiggles by 12px in a random direction. The velocity buffer records
  // the position change and the postprocess smears it. After 80ms the
  // shake ends and the rect is exactly where it should be.
  // ============================================================
  const heroRect = makeRect({
    x: HERO_X, y: HERO_Y, w: HERO_W, h: HERO_H,
    radius: 4,
    fill: { r: 0.08, g: 0.07, b: 0.06, a: 1 },
    borderWidths: [1, 1, 1, 1],
    borderColor: { r: 0.18, g: 0.16, b: 0.14, a: 1 },
    // Glow that breathes gently behind the hero
    filter: { blur: 0, brightness: 1, contrast: 1.05, saturate: 1.1, hueRotate: 0, invert: 0 },
  });
  // imageUrl must be set post-makeRect — makeRect doesn't propagate
  // partial.imageUrl through (same bug as onTap).
  heroRect.imageUrl = `/products/maison-verre/no2/view-${VIEWS[0]!.key}.svg`;
  heroRect.update = (n) => {
    // Apply the shake when active
    if (T() < heroShakeUntil) {
      // Decay the shake so it doesn't snap harshly at the end
      const t = (heroShakeUntil - T()) / 80;
      n.x = HERO_X + heroShakeX * t;
      n.y = HERO_Y + heroShakeY * t;
    } else {
      n.x = HERO_X;
      n.y = HERO_Y;
    }
  };
  addNode(scene, heroRect); z++;

  // ============================================================
  // PRICE + CTA (right side, below the thumbnail column)
  // ============================================================
  const PRICE_Y = THUMBS_Y0 + THUMBS_BLOCK_H + 36;
  addNode(scene, makeText({
    x: THUMBS_X, y: PRICE_Y, w: 240, h: 38,
    text: "320 €",
    fontSize: 32, fontWeight: 600,
    textColor: { r: 0.95, g: 0.9, b: 0.8, a: 1 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;
  addNode(scene, makeText({
    x: THUMBS_X, y: PRICE_Y + 44, w: 240, h: 14,
    text: "100 ml · France · édition limitée",
    fontSize: 10, fontWeight: 400, letterSpacing: 2,
    textColor: { r: 0.55, g: 0.5, b: 0.42, a: 1 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // Add-to-bag CTA — solid gold pill with breathing brightness.
  const CTA_W = 220, CTA_H = 50;
  const CTA_X = THUMBS_X;
  const CTA_Y = PRICE_Y + 80;
  const cta = makeRect({
    x: CTA_X, y: CTA_Y, w: CTA_W, h: CTA_H,
    radius: 25,
    fill: { r: 0.78, g: 0.62, b: 0.32, a: 1 },
    gradientEnd: { r: 0.95, g: 0.82, b: 0.55, a: 1 },
    gradientAngle: Math.PI / 2,
    filter: { blur: 0, brightness: 1, contrast: 1, saturate: 1.2, hueRotate: 0, invert: 0 },
    update: (n) => {
      const t = T();
      const k = 1 + Math.sin(t / 900) * 0.025;
      n.w = CTA_W * k;
      n.h = CTA_H * k;
      n.x = CTA_X - (CTA_W * k - CTA_W) / 2;
      n.y = CTA_Y - (CTA_H * k - CTA_H) / 2;
    },
  });
  addNode(scene, cta); z++;
  addNode(scene, makeText({
    x: CTA_X, y: CTA_Y + 17, w: CTA_W, h: 18,
    text: "AJOUTER AU PANIER",
    fontSize: 12, fontWeight: 700, letterSpacing: 3,
    textAlign: "center",
    textColor: { r: 0.08, g: 0.06, b: 0.04, a: 1 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // ============================================================
  // 6 THUMBNAILS
  // Each is its own image node pointing at a different angle SVG.
  // The thumbnail rect has onTap set after addNode (because makeRect
  // doesn't copy onTap through — see lab.ts fix). The tap callback
  // updates the hero's imageUrl AND triggers the shake.
  // ============================================================
  const thumbNodes: SceneNode[] = [];
  for (let i = 0; i < VIEWS.length; i++) {
    const view = VIEWS[i]!;
    const tx = THUMBS_X;
    const ty = THUMBS_Y0 + i * (THUMB_H + THUMB_GAP);
    const hue = i * 18; // subtle per-thumb chromatic variation
    const isSel = i === activeIdx;
    const thumbBg = makeRect({
      x: tx, y: ty, w: THUMB_W, h: THUMB_H,
      radius: 4,
      fill: { r: 0.08, g: 0.07, b: 0.06, a: 1 },
      borderWidths: [isSel ? 2 : 1, isSel ? 2 : 1, isSel ? 2 : 1, isSel ? 2 : 1],
      borderColor: isSel
        ? { r: 0.92, g: 0.78, b: 0.45, a: 1 }
        : { r: 0.22, g: 0.2, b: 0.16, a: 1 },
      filter: {
        blur: 0, brightness: 1, contrast: 1.05,
        saturate: 1.2 + i * 0.06,
        hueRotate: hue,
        invert: 0,
      },
    });
    thumbBg.imageUrl = `/products/maison-verre/no2/view-${view.key}.svg`;
    // Hover lift + brightness pulse, plus selection ring tracking
    thumbBg.update = (n) => {
      const isHovered = scene.hoveredNode === n.id;
      const sel = i === activeIdx;
      // Scale slightly on hover
      const lift = isHovered ? 1.06 : 1.0;
      const cx = tx + THUMB_W / 2;
      const cy = ty + THUMB_H / 2;
      n.scaleX = n.scaleY = lift;
      n.x = cx - (THUMB_W * lift) / 2;
      n.y = cy - (THUMB_H * lift) / 2;
      n.borderColor = sel
        ? { r: 0.92, g: 0.78, b: 0.45, a: 1 }
        : isHovered
          ? { r: 0.55, g: 0.5, b: 0.42, a: 1 }
          : { r: 0.22, g: 0.2, b: 0.16, a: 1 };
      n.borderWidths = sel ? [2, 2, 2, 2] : [1, 1, 1, 1];
    };
    // Assign onTap AFTER addNode — makeRect doesn't propagate onTap
    // from its partial arg (see the fix in lab.ts's tab bar).
    thumbBg.onTap = () => {
      if (i === activeIdx) return;
      activeIdx = i;
      // Swap the hero's image to the new angle.
      heroRect.imageUrl = `/products/maison-verre/no2/view-${view.key}.svg`;
      // Trigger the hero shake — random direction, 12px, 80ms duration.
      // The motion-blur postprocess picks this up and smears the swap.
      const angle = Math.random() * Math.PI * 2;
      heroShakeX = Math.cos(angle) * 12;
      heroShakeY = Math.sin(angle) * 12;
      heroShakeUntil = T() + 80;
    };
    thumbNodes.push(thumbBg);
    addNode(scene, thumbBg); z++;

    // Tiny label below each thumb (e.g. "Face", "Profil")
    addNode(scene, makeText({
      x: tx - 10, y: ty + THUMB_H + 4, w: THUMB_W + 20, h: 12,
      text: view.label.toUpperCase(),
      fontSize: 8, fontWeight: 600, letterSpacing: 2,
      textAlign: "center",
      textColor: { r: 0.42, g: 0.38, b: 0.32, a: 1 },
      fontFamily: "system-ui, -apple-system, sans-serif",
    })); z++;
  }

  // ============================================================
  // FOOTER hairline + meta
  // ============================================================
  addNode(scene, makeText({
    x: HERO_PAD, y: VH - 36, w: VW - HERO_PAD * 2, h: 12,
    text: "LIVRAISON OFFERTE  ·  RETOURS 30 JOURS  ·  ÉDITION NUMÉROTÉE",
    fontSize: 9, fontWeight: 600, letterSpacing: 4,
    textAlign: "center",
    textColor: { r: 0.4, g: 0.36, b: 0.3, a: 1 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // Expose activeIdx for tests + the demo harness.
  (scene as unknown as { __getActiveThumb?: () => number }).__getActiveThumb = () => activeIdx;

  return scene;
}